﻿import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { ApiUnauthorizedError, ApiForbiddenError } from './errors';

export interface QueryClientOptions {
  /**
   * Callback global appelé pour chaque erreur de query ou mutation.
   * Typiquement utilisé pour déclencher un toast (voir useGsQueryToastHandler de @gs/ui).
   * Kind permet de distinguer les erreurs de query (lecture) vs mutation (écriture).
   */
  onError?: (err: Error, kind: 'query' | 'mutation') => void;
}

/**
 * Fabrique un QueryClient avec la config GS standard :
 *   - retry intelligent : 401/403 ne retry PAS (l'utilisateur doit être auth différemment)
 *   - retry max 1 pour les autres erreurs (évite de spammer un backend en panne)
 *   - staleTime 5 min (cohérent toutes apps — moins de requêtes inutiles)
 *   - refetchOnWindowFocus : oui (user qui revient voit données fraîches)
 *   - refetchOnReconnect : oui (reprend après coupure réseau)
 *   - onError global (UX-02) : si fourni, notifié pour chaque erreur de query/mutation
 */
export function createGsQueryClient(opts: QueryClientOptions = {}): QueryClient {
  const onError = opts.onError;

  return new QueryClient({
    queryCache: onError
      ? new QueryCache({ onError: (err) => onError(err as Error, 'query') })
      : undefined,
    mutationCache: onError
      ? new MutationCache({ onError: (err) => onError(err as Error, 'mutation') })
      : undefined,
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          // Ne JAMAIS retry les 401/403 — le retry 401 est déjà fait dans apiFetch
          if (error instanceof ApiUnauthorizedError) return false;
          if (error instanceof ApiForbiddenError) return false;
          // Retry max 1 fois pour 5xx et erreurs réseau
          return failureCount < 1;
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
        refetchOnWindowFocus: true, // user revient → refresh
        refetchOnReconnect: true, // reprise réseau → refresh
      },
      mutations: {
        retry: false, // mutations jamais retry automatiquement (risque double POST)
      },
    },
  });
}
